export const getSpecificLayout = (
  allQuestions,
  layoutControlType,
  findPropertyInterfaceKey
) => {
  // Find the layout with the given type
  // and which contains the specified question
  return allQuestions.find((lay) => {
    return (
      lay.AnswerControlType === layoutControlType &&
      lay.Questions &&
      lay.Questions.some((q) => q.InterfaceKey === findPropertyInterfaceKey)
    );
  });
};

export const resetAddressLayoutAnswers = (
  allQuestions,
  layoutControlType,
  findPropertyInterfaceKey
) => {
  const layout = getSpecificLayout(
    allQuestions,
    layoutControlType,
    findPropertyInterfaceKey
  );

  const addressQuestions = layout.Questions.filter(
    (q) => q.InterfaceKey !== findPropertyInterfaceKey
  );
  addressQuestions.forEach((question) => {
    question.Value = "";
  });
};

export const updateAddressLayoutAnswers = (
  allQuestions,
  layoutControlType,
  findPropertyInterfaceKey,
  selectedAddress
) => {
  const layout = getSpecificLayout(
    allQuestions,
    layoutControlType,
    findPropertyInterfaceKey
  );

  const addressQuestions = layout.Questions.filter(
    (q) => q.InterfaceKey !== findPropertyInterfaceKey
  );
  addressQuestions.forEach((question, index) => {
    // Copy across the selectedAddress fields by naive index
    // Requires both collections to be in the same order
    // TODO - This is a little fragile; we should copy by InterfaceKey
    question.Value = Object.values(selectedAddress)[index];
  });
};

export const updateAddressFinderAnswers = (
  allQuestions,
  listAddressInterfaceKeyQuestions,
  selectedAddress
) => {
  const addressQuestions = allQuestions.filter((item) => {
    return listAddressInterfaceKeyQuestions.includes(item.InterfaceKey);
  });
  addressQuestions.forEach((question) => {
    question.Value = selectedAddress[question.InterfaceKey];
  });
};

export const loqateEntriesToList = (entries) => {
  if (entries[0]?.Error) return (entries = []);
  return entries.map((entry) => {
    return {
      text: `${entry.Text} ${entry.Description}`,
      id: entry.Id,
      type: entry.Type.toLowerCase(),
    };
  });
};

export const setInsuredAddress = (question, data) => {
  if (question.InterfaceKey !== "FindProperty") return;

  const insuredAddressStreet = data.find(
    (question) => question.InterfaceKey === "InsuredAddressStreet"
  )?.Value;

  const insuredAddressTown = data.find(
    (question) => question.InterfaceKey === "InsuredAddressTown"
  )?.Value;

  const insuredAddressPostCode = data.find(
    (question) => question.InterfaceKey === "InsuredAddressPostCode"
  )?.Value;

  const validAddress =
    insuredAddressStreet && insuredAddressTown && insuredAddressPostCode;

  if (validAddress) {
    question.Value = generateAddress(
      insuredAddressStreet,
      insuredAddressTown,
      insuredAddressPostCode
    );
  }

  return validAddress;
};

export const setNonInsuredAddress = (question, data) => {
  let nonInsuredAddressStreet = "";
  let nonInsuredAddressTown = "";
  let nonInsuredAddressPostCode = "";
  if (question.InterfaceKey === "CorrespondenceFindProperty") {
    nonInsuredAddressStreet = data.find(
      (question) => question.InterfaceKey === "CorrespondenceStreet"
    )?.Value;

    nonInsuredAddressTown = data.find(
      (question) => question.InterfaceKey === "CorrespondenceTown"
    )?.Value;

    nonInsuredAddressPostCode = data.find(
      (question) => question.InterfaceKey === "CorrespondencePostCode"
    )?.Value;
  } else if (question.InterfaceKey === "SolicitorFindProperty") {
    nonInsuredAddressStreet = data.find(
      (question) => question.InterfaceKey === "SolicitorAddressStreet"
    )?.Value;

    nonInsuredAddressTown = data.find(
      (question) => question.InterfaceKey === "SolicitorAddressTown"
    )?.Value;

    nonInsuredAddressPostCode = data.find(
      (question) => question.InterfaceKey === "SolicitorAddressPostCode"
    )?.Value;
  } else {
    return;
  }

  if (
    nonInsuredAddressStreet &&
    nonInsuredAddressTown &&
    nonInsuredAddressPostCode
  ) {
    question.Value = generateAddress(
      nonInsuredAddressStreet,
      nonInsuredAddressTown,
      nonInsuredAddressPostCode
    );
  } else {
    question.Value = "";
  }
};

export const setAddressFormVisibility = (
  findPropertyQuestion,
  otherAddressQuestions
) => {
  if (
    findPropertyQuestion.Value === null ||
    findPropertyQuestion.Value.length === 0
  ) {
    return otherAddressQuestions.some((q) => q.Value.length);
  }
};

/**
 * return combined address of street, town, and postCode, return null if any value is null or empty string
 * @param {{findProperty: {Value: String}, street: {Value: String}, town: {Value: String}, postCode: {Value: String}}} formAddressData
 */
export const generateFormattedAddress = (formAddressData) => {
  const street = formAddressData.street.Value;
  const town = formAddressData.town.Value;
  const postCode = formAddressData.postCode.Value;

  const validAddress = !!street && !!town && !!postCode;

  return validAddress ? generateAddress(street, town, postCode) : null;
};

/**
 *
 * @param {String} street
 * @param {String} town
 * @param {String} postCode
 * @returns
 */
export const generateAddress = (street, town, postCode) => {
  return `${street}, ${town}, ${postCode}`;
};

export const formatAddress = (address, truncateEnd = 30) => {
  let formattedAddress = {};

  const addressLine1Length = address.Line1?.length;
  const addressLine2Length = address.Line2?.length;
  const addressLine3Length = address.Line3?.length;
  const addressLine4Length = address.Line4?.length;

  if (
    addressLine2Length > 0 &&
    addressLine3Length > 0 &&
    addressLine4Length > 0
  ) {
    formattedAddress.Line1 = address.Line1 + ", " + address.Line2;
    formattedAddress.Line2 = address.Line3 + ", " + address.Line4;
  } else if (addressLine2Length > 0 && addressLine3Length > 0) {
    formattedAddress.Line1 = address.Line1 + ", " + address.Line2;
    formattedAddress.Line2 = address.Line3;
  } else {
    formattedAddress.Line1 = address.Line1;
    formattedAddress.Line2 = address.Line2;
  }

  // If the address has a company name, we need to format it differently
  const companyNameLength = address.Company?.length;

  if (companyNameLength > 15) {
    formattedAddress.Line1 = address.Company;
    formattedAddress.Line2 = address.Line1 + ", " + address.Line2;
  } else if (companyNameLength + addressLine1Length > 30) {
    formattedAddress.Line1 = address.Company;
    formattedAddress.Line2 = address.Line1 + ", " + address.Line2;
  } else if (companyNameLength > 0) {
    formattedAddress.Line1 = address.Company + ", " + address.Line1;
    formattedAddress.Line2 =
      address.Line2 && address.Line3
        ? address.Line2 + ", " + address.Line3
        : address.Line2 || address.Line3;
  }

  return {
    street: formattedAddress.Line1.slice(0, truncateEnd)
      .trim()
      .replace(/[^a-zA-Z0-9\s\-\\',\\.\\/]/g, ""),
    district: formattedAddress.Line2.slice(0, truncateEnd).trim(),
  };
};

export const addressSelected = (response, addressForm, truncateEnd = 30) => {
  const item = response.data.Items[0];
  const propertiesAddress = formatAddress(item, truncateEnd);
  addressForm.Street.Value = propertiesAddress.street;
  addressForm.PostCode.Value = item.PostalCode;
  addressForm.District.Value = propertiesAddress.district;
  addressForm.Town.Value = item.City;
  addressForm.County.Value = item.Province;

  // remove duplicate address values
  addressForm.County.Value =
    addressForm.Town.Value === addressForm.County.Value
      ? ""
      : addressForm.County.Value;

  // remove duplicate address values
  addressForm.District.Value =
    addressForm.Street.Value === addressForm.District.Value
      ? ""
      : addressForm.District.Value;
};

export const getSelectedAddress = (
  response,
  interfaceKeyPrefix,
  truncateEnd = 30
) => {
  // Process the Loqate response into an object
  // It contains a single `Item` in `Items`.
  // The order of the keys matters because of how we later map them in updateAllAddressQuestions
  const item = response.data.Items[0];
  const propertiesAddress = formatAddress(item, truncateEnd);
  // remove duplicate address values
  propertiesAddress.district =
    propertiesAddress.street === propertiesAddress.district
      ? ""
      : propertiesAddress.district;
  const model = {
    [interfaceKeyPrefix + "PostCode"]: item.PostalCode,
    [interfaceKeyPrefix + "Street"]: propertiesAddress.street,
    [interfaceKeyPrefix + "District"]: propertiesAddress.district,
    [interfaceKeyPrefix + "Town"]: item.City,
    [interfaceKeyPrefix + "County"]: item.Province,
  };
  return model;
};
